import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { CurrencyInput } from '../../components/CurrencyInput';
import { FiUser, FiLock, FiMail, FiPhone, FiMapPin, FiBriefcase, FiDollarSign, FiUsers } from 'react-icons/fi';
import { API_URL } from '../../api/client';

interface ProfileData {
  user_id?: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  date_of_birth?: string;
  bvn?: string;
  address?: string;
  city?: string;
  state?: string;
  lga?: string;
  employer?: string;
  occupation?: string;
  monthly_income?: number;
  employment_status?: string;
  employment_start_date?: string;
  business_name?: string;
  business_type?: string;
  business_registration_number?: string;
  tax_identification_number?: string;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  next_of_kin_name?: string;
  next_of_kin_phone?: string;
  next_of_kin_relationship?: string;
}

interface PasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export const ProfileSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [passwordData, setPasswordData] = useState<PasswordData>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      setProfile(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field: keyof ProfileData, value: string | number) => {
    if (!profile) return;
    setProfile({
      ...profile,
      [field]: value,
    });
    setSuccess(null);
  };

  const handlePasswordChange = (field: keyof PasswordData, value: string) => {
    setPasswordData({
      ...passwordData,
      [field]: value,
    });
    setSuccess(null);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      setError('All password fields are required');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/users/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to change password');
      }

      setSuccess('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h1>
        <p className="text-base text-gray-600">
          Manage your profile information and account security
        </p>
      </div>

      {error && <ErrorMessage message={error} />}
      {success && (
        <div style={{
          padding: '1rem 1.25rem',
          backgroundColor: '#d1fae5',
          color: '#065f46',
          border: '1px solid #6ee7b7',
          borderRadius: '0.75rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.25rem' }}>✓</span>
          <span>{success}</span>
        </div>
      )}

      {/* Modern Tab Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex gap-2 p-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeTab === 'profile'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiUser size={18} />
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeTab === 'password'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiLock size={18} />
              Change Password
            </button>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {activeTab === 'profile' && profile && (
            <form onSubmit={handleSaveProfile} className="max-w-4xl">
              {/* Personal Information Section */}
              <section className="mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FiUser className="text-blue-600" size={20} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profile.first_name}
                      onChange={(e) => handleProfileChange('first_name', e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={profile.last_name}
                      onChange={(e) => handleProfileChange('last_name', e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Middle Name</label>
                    <input
                      type="text"
                      value={profile.middle_name || ''}
                      onChange={(e) => handleProfileChange('middle_name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
                    <input
                      type="date"
                      value={profile.date_of_birth || ''}
                      onChange={(e) => handleProfileChange('date_of_birth', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 cursor-not-allowed text-gray-500"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">Email cannot be changed</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 cursor-not-allowed text-gray-500"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">Phone cannot be changed</p>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">BVN</label>
                  <input
                    type="text"
                    value={profile.bvn || ''}
                    onChange={(e) => handleProfileChange('bvn', e.target.value)}
                    maxLength={11}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Enter your 11-digit BVN"
                  />
                </div>
              </section>

              {/* Address Information Section */}
              <section className="mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <FiMapPin className="text-green-600" size={20} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Address Information</h2>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={profile.address || ''}
                      onChange={(e) => handleProfileChange('address', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter your full address"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={profile.city || ''}
                        onChange={(e) => handleProfileChange('city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={profile.state || ''}
                        onChange={(e) => handleProfileChange('state', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">LGA</label>
                    <input
                      type="text"
                      value={profile.lga || ''}
                      onChange={(e) => handleProfileChange('lga', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>
              </section>

              {/* Employment Information Section */}
              <section className="mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FiBriefcase className="text-purple-600" size={20} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Employment Information</h2>
                </div>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Employer</label>
                      <input
                        type="text"
                        value={profile.employer || ''}
                        onChange={(e) => handleProfileChange('employer', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Occupation</label>
                      <input
                        type="text"
                        value={profile.occupation || ''}
                        onChange={(e) => handleProfileChange('occupation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <CurrencyInput
                        id="monthly_income"
                        name="monthly_income"
                        label="Monthly Income"
                        value={profile.monthly_income || 0}
                        onChange={(value) => {
                          handleProfileChange('monthly_income', value);
                        }}
                        placeholder="Enter monthly income"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Employment Status</label>
                      <select
                        value={profile.employment_status || ''}
                        onChange={(e) => handleProfileChange('employment_status', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      >
                        <option value="">Select status</option>
                        <option value="employed">Employed</option>
                        <option value="self-employed">Self-Employed</option>
                        <option value="unemployed">Unemployed</option>
                      </select>
                    </div>
                  </div>
                </div>
              </section>

              {/* Bank Account Section */}
              <section className="mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FiDollarSign className="text-amber-600" size={20} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Bank Account</h2>
                </div>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Name</label>
                      <input
                        type="text"
                        value={profile.bank_name || ''}
                        onChange={(e) => handleProfileChange('bank_name', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                      <input
                        type="text"
                        value={profile.account_number || ''}
                        onChange={(e) => handleProfileChange('account_number', e.target.value)}
                        maxLength={10}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Name</label>
                    <input
                      type="text"
                      value={profile.account_name || ''}
                      onChange={(e) => handleProfileChange('account_name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>
              </section>

              {/* Next of Kin Section */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                    <FiUsers className="text-rose-600" size={20} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Next of Kin</h2>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Next of Kin Name</label>
                    <input
                      type="text"
                      value={profile.next_of_kin_name || ''}
                      onChange={(e) => handleProfileChange('next_of_kin_name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Next of Kin Phone</label>
                      <input
                        type="tel"
                        value={profile.next_of_kin_phone || ''}
                        onChange={(e) => handleProfileChange('next_of_kin_phone', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Relationship</label>
                      <input
                        type="text"
                        value={profile.next_of_kin_relationship || ''}
                        onChange={(e) => handleProfileChange('next_of_kin_relationship', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Form Actions */}
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <Button
                  type="submit"
                  variant="primary"
                  loading={saving}
                  disabled={saving}
                >
                  Save Changes
                </Button>
                <Button
                  type="button"
                  onClick={fetchProfile}
                  variant="secondary"
                >
                  Reset
                </Button>
              </div>
            </form>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handleChangePassword} className="max-w-2xl">
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FiLock className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Change Your Password</h2>
                    <p className="text-sm text-gray-600">Ensure your password is at least 8 characters long</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                      required
                      minLength={8}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter new password"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">Must be at least 8 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              </section>

              <div className="pt-6 border-t border-gray-200">
                <Button
                  type="submit"
                  variant="primary"
                  loading={saving}
                  disabled={saving}
                >
                  Change Password
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .grid.grid-cols-1.md\\:grid-cols-2 {
            grid-template-columns: 1fr !important;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .max-w-7xl {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          .mb-8 h1 {
            font-size: 1.75rem !important;
          }

          .mb-8 p {
            font-size: 0.9375rem !important;
          }

          .flex.gap-2.p-2 {
            flex-direction: column;
            padding: 0.5rem !important;
          }

          .flex.gap-2.p-2 button {
            width: 100%;
            justify-content: center;
          }

          .p-6.md\\:p-8 {
            padding: 1.25rem !important;
          }

          .max-w-4xl,
          .max-w-2xl {
            max-width: 100% !important;
          }

          .flex.items-center.gap-3.mb-6 h2 {
            font-size: 1.125rem !important;
          }

          .grid.grid-cols-1.md\\:grid-cols-2 {
            grid-template-columns: 1fr !important;
          }

          .w-10.h-10 {
            width: 2.5rem !important;
            height: 2.5rem !important;
          }

          .w-12.h-12 {
            width: 2.75rem !important;
            height: 2.75rem !important;
          }

          label.block {
            font-size: 0.875rem !important;
          }

          input.w-full,
          select.w-full,
          textarea.w-full {
            padding: 0.875rem !important;
            font-size: 1rem !important;
          }

          .flex.gap-3.pt-6 {
            flex-direction: column;
          }

          .flex.gap-3.pt-6 button {
            width: 100%;
          }

          .pt-6 button {
            width: 100%;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .max-w-7xl {
            padding-left: 0.875rem !important;
            padding-right: 0.875rem !important;
          }

          .mb-8 h1 {
            font-size: 1.5rem !important;
          }

          .p-6.md\\:p-8 {
            padding: 1rem !important;
          }

          .flex.items-center.gap-3.mb-6 {
            gap: 0.625rem !important;
          }

          .w-10.h-10 {
            width: 2.25rem !important;
            height: 2.25rem !important;
          }

          input.w-full,
          select.w-full,
          textarea.w-full {
            padding: 0.75rem !important;
            font-size: 0.9375rem !important;
          }

          .space-y-5 {
            gap: 1rem !important;
          }

          .grid.gap-5 {
            gap: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};
